/**
 * First-connect coach-mark visibility store.
 *
 * The coach is a one-time orientation callout shown only when the bridge lands
 * in Table Explorer. Visibility lives in memory: it is set true exactly once
 * per install (the bridge fires once, guarded by a persisted flag), so it never
 * needs to re-appear after a reload — the persisted `BRIDGE_FIRED` flag prevents
 * the bridge (and therefore the coach) from ever running a second time.
 */

import { create } from 'zustand';

type FirstConnectCoachState = {
  readonly visible: boolean;
  /** The table the bridge landed on — interpolated into the coach copy. */
  readonly tableName: string | null;
  readonly show: (tableName: string) => void;
  readonly hide: () => void;
};

export const useFirstConnectCoachStore = create<FirstConnectCoachState>((set) => ({
  visible: false,
  tableName: null,
  show: (tableName: string) => set({ visible: true, tableName }),
  hide: () => set({ visible: false }),
}));

/** How the coach was dismissed (low-cardinality; safe for analytics). */
export type CoachDismissAction = 'close' | 'new_query';

/**
 * Hide the coach and record how it was dismissed. Used by both the ✕ button
 * and the New Query button (which dismisses the coach as it opens a tab), so
 * the analytics + hide happen in exactly one place. No-ops if already hidden.
 */
export function dismissFirstConnectCoach(action: CoachDismissAction): void {
  if (!useFirstConnectCoachStore.getState().visible) return;
  useFirstConnectCoachStore.getState().hide();
  window.electronAPI?.analytics
    ?.track('ui:first_connect_coach_dismissed', { action })
    ?.catch(() => {});
}
